import React, { useRef, useCallback, useEffect } from 'react';
import { Button, CircularProgress, useTheme, useMediaQuery, SxProps, Theme } from '@mui/material';
import { getButtonColor, getButtonHoverColor } from '../../theme/themeUtils';
import { responsiveFontSize } from '../../util/responsiveUtils';
import { alpha } from '@mui/material/styles';

export interface CustomButtonProps {
    // Content
    children: React.ReactNode;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    
    // Appearance
    variant?: 'contained' | 'outlined' | 'text';
    size?: 'small' | 'medium' | 'large' | 'xlarge';
    color?: string;                    // Primary color for outlined border/text, or contained background
    backgroundColor?: string;          // Override background for contained variant
    textColor?: string;                // Override text color
    fullWidth?: boolean;
    
    // State
    disabled?: boolean;
    loading?: boolean;
    
    // Behavior
    onClick?: () => void;
    debounceMs?: number;
    
    // Customization
    sx?: SxProps<Theme>;
    
    // Optional: for form submission
    type?: 'button' | 'submit' | 'reset';
}

// Debounce time in ms to prevent double-taps
const DEFAULT_DEBOUNCE_MS = 400;

export const CustomButton: React.FC<CustomButtonProps> = ({
    children,
    startIcon,
    endIcon,
    variant = 'contained',
    size = 'large',
    color,
    backgroundColor,
    textColor,
    fullWidth = false,
    disabled = false,
    loading = false,
    onClick,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    sx = {},
    type = 'button',
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // Refs for debouncing to prevent rapid multiple taps
    const processingRef = useRef(false);
    const touchHandledRef = useRef(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    
    // Derive colors from theme or props
    // Handle 'inherit' color - use theme text color instead
    const resolvedColor = color === 'inherit' ? theme.palette.text.primary : (color || getButtonColor(theme));
    const primaryColor = resolvedColor;
    const hoverColor = getButtonHoverColor(theme);
    const bgColor = backgroundColor || primaryColor;
    
    // Reset guards when onClick changes (e.g., navigation)
    useEffect(() => {
        processingRef.current = false;
        touchHandledRef.current = false;
        
        // Blur button on mount to prevent "already selected" issue
        if (buttonRef.current) {
            buttonRef.current.blur();
        }
    }, [onClick]);
    
    // Touch handler for iOS
    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        // For submit buttons, allow default form submission behavior
        // Only prevent default for non-submit buttons or disabled/loading submit buttons
        if (type === 'submit') {
            // For submit buttons, only prevent if disabled or loading
            if (disabled || loading || processingRef.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            // On mobile, explicitly trigger form submission by clicking the button
            // This ensures the form's onSubmit handler is called
            // Don't set touchHandledRef here - let the click handler process the form submission
            if (buttonRef.current) {
                // Trigger a click event to ensure form submission works on mobile
                buttonRef.current.click();
            }
            return;
        }
        
        // For non-submit buttons, prevent default as usual
        e.preventDefault();
        e.stopPropagation();
        
        if (processingRef.current || disabled || loading) {
            return;
        }
        
        touchHandledRef.current = true;
        processingRef.current = true;
        
        if (buttonRef.current) {
            buttonRef.current.blur();
        }
        
        onClick?.();
        
        setTimeout(() => {
            processingRef.current = false;
            touchHandledRef.current = false;
        }, debounceMs);
    }, [onClick, disabled, loading, debounceMs, type]);
    
    // Click handler for desktop/mouse
    const handleClick = useCallback((e: React.MouseEvent) => {
        // For submit buttons, allow default form submission behavior
        // Only prevent default for non-submit buttons or disabled/loading submit buttons
        if (type === 'submit') {
            // For submit buttons, only prevent if disabled or loading
            if (disabled || loading || processingRef.current || touchHandledRef.current) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            // Allow form to submit naturally - don't prevent default
            // Don't call onClick for submit buttons - let form handle it
            return;
        }
        
        // For non-submit buttons, prevent default as usual
        e.preventDefault();
        e.stopPropagation();
        
        if (touchHandledRef.current || processingRef.current || disabled || loading) {
            return;
        }
        
        processingRef.current = true;
        
        if (buttonRef.current) {
            buttonRef.current.blur();
        }
        
        onClick?.();
        
        setTimeout(() => {
            processingRef.current = false;
        }, debounceMs);
    }, [onClick, disabled, loading, debounceMs, type]);
    
    // Touch start - prevent bubbling
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        e.stopPropagation();
    }, []);

    // Size configurations
    const sizeConfig = {
        small: {
            py: { xs: 1, sm: 1.2, md: 1.5 },
            px: { xs: 2, sm: 2.5, md: 3 },
            fontSize: responsiveFontSize.buttonSmall,
            minWidth: { xs: 100, sm: 120, md: 140 },
        },
        medium: {
            py: { xs: 1.5, sm: 1.8, md: 2 },
            px: { xs: 3, sm: 3.5, md: 4 },
            fontSize: responsiveFontSize.buttonMedium,
            minWidth: { xs: 140, sm: 160, md: 180 },
        },
        large: {
            py: { xs: 1.8, sm: 2.2, md: 2.5 },
            px: { xs: 3.6, sm: 3.7, md: 4.7 },
            fontSize: responsiveFontSize.buttonLarge,
            minWidth: { xs: 213, sm: 185.6, md: 210 },
        },
        xlarge: {
            py: { xs: 2.145, sm: 2.64, md: 3.0 },
            px: { xs: 4.29, sm: 3.96, md: 5.4 },
            fontSize: responsiveFontSize.buttonXlarge,
            minWidth: { xs: 286, sm: 211.2, md: 240 },
        },
    };

    const currentSize = sizeConfig[size];

    // Map custom sizes to valid MUI Button sizes
    // Since we override all size styles via sx, this is just for MUI's internal handling
    const muiSize = size === 'xlarge' ? 'large' : size === 'large' ? 'medium' : size;

    // iOS-safe base styles
    const iosBaseStyles = {
        WebkitTapHighlightColor: 'transparent',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        touchAction: 'manipulation',
        cursor: 'pointer',
        transition: 'none',
        transform: 'none',
        '&:focus': {
            outline: 'none',
            boxShadow: 6,
        },
        '&:focus-visible': {
            outline: 'none',
        },
        '&:active': {
            transform: 'none',
            boxShadow: 6,
        },
        '@media (hover: hover)': {
            '&:hover': {
                transform: 'none',
            },
        },
    };

    // Variant-specific styles
    const getVariantStyles = (): Record<string, unknown> => {
        switch (variant) {
            case 'contained':
                return {
                    backgroundColor: bgColor,
                    color: textColor || '#ffffff',
                    '@media (hover: hover)': {
                        '&:hover': {
                            backgroundColor: isMobile ? bgColor : hoverColor,
                            transform: 'none',
                            boxShadow: { xs: 6, md: 8 },
                        },
                    },
                    '&:active': {
                        backgroundColor: hoverColor,
                        transform: 'none',
                        boxShadow: 6,
                    },
                    '&:disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                        pointerEvents: 'auto',
                        cursor: 'not-allowed',
                    },
                };
            case 'outlined':
                return {
                    color: textColor || primaryColor,
                    borderColor: primaryColor,
                    backgroundColor: 'transparent',
                    '@media (hover: hover)': {
                        '&:hover': {
                            borderColor: isMobile ? primaryColor : hoverColor,
                            backgroundColor: isMobile ? 'transparent' : alpha(primaryColor, 0.08),
                            transform: 'none',
                            boxShadow: { xs: 6, md: 8 },
                        },
                    },
                    '&:active': {
                        backgroundColor: alpha(primaryColor, 0.12),
                        transform: 'none',
                        boxShadow: 6,
                    },
                    '&:disabled': {
                        pointerEvents: 'auto',
                        cursor: 'not-allowed',
                    },
                };
            case 'text':
                // Handle 'inherit' for textColor as well
                const resolvedTextColor = textColor === 'inherit' 
                    ? theme.palette.text.primary 
                    : (textColor || primaryColor);
                return {
                    color: resolvedTextColor,
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                    '@media (hover: hover)': {
                        '&:hover': {
                            backgroundColor: isMobile ? 'transparent' : alpha(primaryColor, 0.08),
                            transform: 'none',
                            boxShadow: 'none',
                        },
                    },
                    '&:active': {
                        backgroundColor: alpha(primaryColor, 0.12),
                        transform: 'none',
                    },
                    '&:disabled': {
                        pointerEvents: 'auto',
                        cursor: 'not-allowed',
                    },
                };
            default:
                return {};
        }
    };

    // Desktop enhancements (only for non-text variants)
    const getDesktopStyles = (): Record<string, unknown> => {
        if (variant === 'text') return {};
        
        if (variant === 'outlined') {
            return {
                '@media (min-width: 900px)': {
                    // No transition on outlined to avoid shake/blink when hovering over buttons inside cards
                    '@media (hover: hover)': {
                        '&:hover': {
                            boxShadow: 8,
                            backgroundColor: alpha(primaryColor, 0.08),
                            borderColor: hoverColor,
                        },
                    },
                    '&:active': {
                        boxShadow: 8,
                        backgroundColor: alpha(primaryColor, 0.12),
                    },
                    '&:focus-visible': {
                        outline: `2px solid ${primaryColor}`,
                        outlineOffset: 2,
                    },
                },
            };
        }
        
        // contained variant
        return {
            '@media (min-width: 900px)': {
                transition: 'all 0.3s ease',
                '@media (hover: hover)': {
                    '&:hover': {
                        boxShadow: 8,
                        backgroundColor: hoverColor,
                    },
                },
                '&:active': {
                    boxShadow: 8,
                },
                '&:focus-visible': {
                    outline: `2px solid ${primaryColor}`,
                    outlineOffset: 2,
                },
            },
        };
    };

    const buttonSx = {
        ...currentSize,
        fontWeight: 600,
        borderRadius: 2,
        boxShadow: variant === 'text' ? 'none' : 6,
        maxWidth: { xs: '90%', sm: 'none' },
        width: fullWidth ? '100%' : { xs: 'auto', sm: 'auto' },
        pointerEvents: 'auto',
        // Auto-center buttons on mobile when in flex containers
        alignSelf: { xs: 'center', sm: 'auto' },
        ...iosBaseStyles,
        ...getVariantStyles(),
        ...getDesktopStyles(),
        ...sx,
    };

    return (
        <Button
            ref={buttonRef}
            variant={variant}
            size={muiSize}
            type={type}
            disabled={disabled || loading}
            onClick={handleClick}
            onTouchEnd={handleTouchEnd}
            onTouchStart={handleTouchStart}
            startIcon={loading ? undefined : startIcon}
            endIcon={loading ? undefined : endIcon}
            sx={buttonSx as SxProps<Theme>}
            tabIndex={isMobile ? -1 : 0}
        >
            {loading ? (
                <CircularProgress size={24} color="inherit" />
            ) : (
                children
            )}
        </Button>
    );
};

export default CustomButton;
